import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface IframeFieldProps {
  question: SurveyQuestion;
  palette?: Palette;
}

// Read-only embed honoring `settings.iframeUrl` + `settings.iframeHeight`.
// Falls back to a friendly placeholder when the URL is missing or malformed
// so the survey still renders cleanly in the builder preview.
const DEFAULT_HEIGHT = 400;

export function IframeField({ question, palette }: IframeFieldProps): JSX.Element {
  const url = question.settings?.iframeUrl?.trim();
  const height = question.settings?.iframeHeight ?? DEFAULT_HEIGHT;

  if (!url || !isSafeUrl(url)) {
    return (
      <div style={placeholderStyle(palette, height)}>
        <span>{url ? 'Invalid embed URL — enter a valid https:// URL' : 'No embed URL configured'}</span>
      </div>
    );
  }

  return (
    <div style={wrapperStyle(height)}>
      <iframe
        src={url}
        title={question.label ?? 'Embedded content'}
        allow="autoplay; fullscreen; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-popups"
        allowFullScreen
        style={iframeStyle()}
      />
    </div>
  );
}

function isSafeUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function wrapperStyle(height: number): CSSProperties {
  return {
    width: '100%',
    height,
    borderRadius: 8,
    overflow: 'hidden',
    background: '#000',
  };
}

function iframeStyle(): CSSProperties {
  return { width: '100%', height: '100%', border: 0, display: 'block' };
}

function placeholderStyle(palette: Palette | undefined, height: number): CSSProperties {
  const muted = palette?.textMuted ?? '#94a3b8';
  return {
    width: '100%',
    minHeight: height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    color: muted,
    border: `2px dashed ${hexWithAlpha(muted, 0.4)}`,
    borderRadius: 8,
    padding: 16,
    textAlign: 'center',
  };
}
